import { Reveal, ChapterHeader } from "./Reveal";

const SERVICES = [
  {
    title: "Individual therapy",
    text: "One-to-one therapy for children, adolescents and adults, paced to you and grounded in collaboration.",
  },
  {
    title: "Couples & family sessions",
    text: "Shared work for partners and families, usually 70 to 90 minutes, and up to 2 hours when the room needs it.",
  },
  {
    title: "Psychometric assessments",
    text: "Careful evaluation of cognitive, emotional and behavioural functioning, with feedback you can actually use. Offered at PAWC and at the Emtonjeni Centre, Wits University (adult or child).",
  },
  {
    title: "Psycho-legal / medico-legal assessment & reporting",
    text: "Thorough, impartial assessment and reporting for legal and insurance contexts.",
  },
  {
    title: "Psychoeducational assessments & feedback",
    text: "Understanding learning profiles and barriers, with clear guidance for school and home.",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-pawc-ink">
    <div className="max-w-6xl mx-auto px-6">
      <ChapterHeader number="06" eyebrow="Services" title="Ways we can work together" light />

      <div className="mt-14">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={0.08 * i}>
            <div
              data-testid={`service-row-${i}`}
              className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline border-t border-pawc-sand/15 py-8 md:py-10 hover:bg-pawc-deep/40 transition-colors duration-500 px-2 md:px-4 rounded-lg"
            >
              <span className="md:col-span-2 font-serif italic text-3xl text-pawc-terra/80">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-pawc-sand">
                {s.title}
              </h3>
              <p className="md:col-span-6 text-sm md:text-base text-pawc-mist/70 leading-relaxed max-w-[55ch]">
                {s.text}
              </p>
            </div>
          </Reveal>
        ))}
        <div className="border-t border-pawc-sand/15" />
      </div>

      <Reveal delay={0.15}>
        <div data-testid="assessment-journey" className="mt-16">
          <p className="text-xs tracking-[0.28em] uppercase text-pawc-terra mb-3">
            How a full assessment unfolds
          </p>
          <div className="relative grid md:grid-cols-3 gap-6">
            <div
              className="hidden md:block absolute top-6 left-[18%] right-[18%] h-px bg-gradient-to-r from-pawc-teal/40 via-pawc-mist/30 to-pawc-teal/40"
              aria-hidden="true"
            />
            {[
              {
                step: "01",
                title: "Intake",
                time: "1 hour",
                text: "We meet, talk through your questions and history, and agree on what the assessment needs to answer.",
              },
              {
                step: "02",
                title: "Testing",
                time: "4 hours",
                text: "Unhurried assessment sessions, which can be split across visits so the process never feels like an exam.",
              },
              {
                step: "03",
                title: "Feedback",
                time: "1 hour",
                text: "We sit with the results together, in plain language, and talk about what they mean for the road ahead.",
              },
            ].map((s) => (
              <div key={s.step} data-testid={`journey-step-${s.title.toLowerCase()}`} className="relative">
                <div className="w-12 h-12 rounded-full bg-pawc-deep border border-pawc-mist/25 flex items-center justify-center mb-5 relative z-10">
                  <span className="font-serif italic text-pawc-terra">{s.step}</span>
                </div>
                <h4 className="font-serif text-2xl text-pawc-sand mb-1">{s.title}</h4>
                <p className="text-xs tracking-[0.2em] uppercase text-pawc-terra/80 mb-3">{s.time}</p>
                <p className="text-sm text-pawc-mist/70 leading-relaxed max-w-[40ch]">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="text-pawc-mist/50 text-xs mt-8">
            About 6 hours in total, offered at PAWC and at the Emtonjeni Centre, Wits University.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div
          data-testid="fees-panel"
          className="mt-16 rounded-3xl border border-pawc-sand/15 bg-pawc-deep/50 p-8 md:p-12 grid md:grid-cols-12 gap-10"
        >
          <div className="md:col-span-4">
            <p className="text-xs tracking-[0.28em] uppercase text-pawc-terra mb-4">
              Fees & Payment
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-pawc-sand leading-snug mb-6">
              Medical aid rates, plainly stated
            </h3>
            <p className="text-pawc-mist/75 text-sm leading-relaxed">
              Payment is welcomed by medical aid, cash, card or EFT. Rates follow
              the current Discovery Health medical aid rates.
            </p>
            <p className="text-pawc-mist/60 text-xs leading-relaxed mt-4">
              Assessments at the Emtonjeni Centre, Wits University follow medical
              aid rates when claimed through medical aid. Cash assessment fees are
              agreed together in discussion.
            </p>
          </div>
          <div className="md:col-span-8">
            <ul>
              {[
                { label: "Individual / adult therapy session", length: "50 to 60 minutes", price: "R1,292.20" },
                { label: "Play therapy session", length: "50 to 60 minutes", price: "R1,292.20" },
                { label: "Family / couples session", length: "80 to 90 minutes", price: "R1,722.80" },
                { label: "Full psychological assessment", length: "about 6 hours in total: intake (1 hour), testing (4 hours), feedback (1 hour)", price: "R7,753.20" },
              ].map((r, i) => (
                <li
                  key={r.label}
                  data-testid={`fee-row-${i}`}
                  className="flex flex-wrap items-baseline justify-between gap-2 border-b border-pawc-sand/10 py-5"
                >
                  <div>
                    <p className="text-pawc-sand text-base md:text-lg font-serif">{r.label}</p>
                    <p className="text-pawc-mist/60 text-xs mt-1">{r.length}</p>
                  </div>
                  <p className="text-pawc-terra font-serif text-xl md:text-2xl">{r.price}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
